import { useState, useEffect } from 'react';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';
import VerLinksButton from './VerLinksButton';

const PAGE_SIZE = 20;

const inputSt = {
  width: '100%', padding: '8px 10px',
  backgroundColor: theme.colors.background,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: '7px', color: theme.colors.text,
  fontSize: '0.88rem', boxSizing: 'border-box', outline: 'none',
};
const labelSt = { display: 'block', color: theme.colors.textMuted, fontSize: '0.82rem', marginBottom: '3px' };
const fieldSt = { marginBottom: '0.8rem' };

const EMPTY_FORM = { id: '', name: '', description: '', price: '', time: '' };

function Services({ token }) {
  const isMobile = useIsMobile();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [primaryImage, setPrimaryImage] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchData = () => {
    setLoading(true);
    fetch(`/api/services?page=${page}`, { headers: authHeaders })
      .then(r => r.ok ? r.json() : { data: [], total: 0 })
      .then(d => { setData(d.data || []); setTotal(d.total || 0); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [page, token]);

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await fetch('/api/services/export', { headers: authHeaders });
      if (!r.ok) return;
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'servicios.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { /* sin conexión */ }
    finally { setExporting(false); }
  };

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setPrimaryImage(null);
    setAdditionalImages([]);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setForm({ id: row.id, name: row.name, description: row.description || '', price: row.price ?? '', time: row.time });
    setPrimaryImage(null);
    setAdditionalImages([]);
    setFormError('');
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setFormError('El nombre es requerido');
    if (!form.time || parseInt(form.time) <= 0) return setFormError('La duración debe ser un número positivo');
    setSubmitting(true);
    setFormError('');
    try {
      const url = editId ? `/api/services/${editId}` : '/api/services';
      const method = editId ? 'PUT' : 'POST';
      const fd = new FormData();
      if (!editId && form.id.trim()) fd.append('id', form.id.trim());
      fd.append('name', form.name.trim());
      fd.append('description', form.description.trim());
      if (form.price !== '') fd.append('price', form.price);
      fd.append('time', parseInt(form.time));
      if (primaryImage) fd.append('image', primaryImage);
      additionalImages.forEach(f => fd.append('additional_images', f));
      const r = await fetch(url, { method, headers: authHeaders, body: fd });
      const json = await r.json();
      if (!r.ok) { setFormError(json.message || 'Error al guardar'); return; }
      setShowForm(false);
      fetchData();
    } catch {
      setFormError('Error de conexión');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await fetch(`/api/services/${id}`, { method: 'DELETE', headers: authHeaders });
      setConfirmDeleteId(null);
      fetchData();
    } catch {
    } finally {
      setDeletingId(null);
    }
  };

  const fmtPrice = (v) => v == null ? '—' : `$${parseFloat(v).toLocaleString('es-CO')}`;
  const fmtTime = (v) => `${v} min`;

  return (
    <div style={{ padding: isMobile ? '1.2rem 0.8rem' : '2rem', maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ color: theme.colors.primary, margin: 0 }}>Servicios</h2>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={handleExport}
            disabled={exporting}
            style={{
              backgroundColor: 'transparent', color: theme.colors.text,
              border: `1px solid ${theme.colors.border}`,
              padding: '8px 16px', borderRadius: '7px', cursor: exporting ? 'not-allowed' : 'pointer', fontWeight: 600,
            }}
          >
            {exporting ? 'Exportando...' : 'Exportar'}
          </button>
          <button
            onClick={openCreate}
            style={{
              backgroundColor: theme.colors.primary, color: '#000', border: 'none',
              padding: '8px 20px', borderRadius: '7px', cursor: 'pointer', fontWeight: 600,
            }}
          >
            + Crear
          </button>
        </div>
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <VerLinksButton />
      </div>

      {loading ? (
        <p style={{ color: theme.colors.textMuted }}>Cargando...</p>
      ) : data.length === 0 ? (
        <p style={{ color: theme.colors.textMuted }}>No hay servicios registrados.</p>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                  {['ID', 'Imagen', 'Nombre', 'Duración', 'Precio', 'Descripción', 'Acciones'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: theme.colors.textMuted, fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map(row => (
                  <tr key={row.id} style={{ borderBottom: `1px solid ${theme.colors.border}22` }}>
                    <td style={{ padding: '8px 10px', color: theme.colors.textMuted, fontFamily: 'monospace', fontSize: '0.82rem' }}>{row.id}</td>
                    <td style={{ padding: '8px 10px' }}>
                      {row.image_link ? <img src={row.image_link} alt="" style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} /> : '—'}
                    </td>
                    <td style={{ padding: '8px 10px', color: theme.colors.text, fontWeight: 500 }}>{row.name}</td>
                    <td style={{ padding: '8px 10px', color: theme.colors.text }}>{fmtTime(row.time)}</td>
                    <td style={{ padding: '8px 10px', color: theme.colors.text }}>{fmtPrice(row.price)}</td>
                    <td style={{ padding: '8px 10px', color: theme.colors.textMuted, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.description || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>
                      <button
                        onClick={() => openEdit(row)}
                        style={{ marginRight: '8px', background: 'none', border: `1px solid ${theme.colors.border}`, color: theme.colors.text, padding: '4px 12px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.82rem' }}
                      >
                        Editar
                      </button>
                      {confirmDeleteId === row.id ? (
                        <>
                          <button
                            onClick={() => handleDelete(row.id)}
                            disabled={deletingId === row.id}
                            style={{ marginRight: '6px', background: '#c0392b', border: 'none', color: '#fff', padding: '4px 12px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.82rem' }}
                          >
                            {deletingId === row.id ? '...' : 'Confirmar'}
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(null)}
                            style={{ background: 'none', border: `1px solid ${theme.colors.border}`, color: theme.colors.textMuted, padding: '4px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.82rem' }}
                          >
                            Cancelar
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setConfirmDeleteId(row.id)}
                          style={{ background: 'none', border: `1px solid #c0392b`, color: '#c0392b', padding: '4px 12px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.82rem' }}
                        >
                          Eliminar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '1rem', alignItems: 'center' }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '5px 12px', borderRadius: '5px', border: `1px solid ${theme.colors.border}`, background: 'none', color: theme.colors.text, cursor: 'pointer' }}>‹</button>
              <span style={{ color: theme.colors.textMuted, fontSize: '0.85rem' }}>Página {page} de {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: '5px 12px', borderRadius: '5px', border: `1px solid ${theme.colors.border}`, background: 'none', color: theme.colors.text, cursor: 'pointer' }}>›</button>
            </div>
          )}
        </>
      )}

      {/* Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '1rem' : 0, overflowY: 'auto' }}>
          <div style={{ backgroundColor: theme.colors.surface, borderRadius: '12px', padding: isMobile ? '1.2rem' : '2rem', width: '100%', maxWidth: '440px', border: `1px solid ${theme.colors.border}`, boxSizing: 'border-box', maxHeight: isMobile ? '90vh' : 'none', overflowY: isMobile ? 'auto' : 'visible' }}>
            <h3 style={{ color: theme.colors.primary, margin: '0 0 1.2rem' }}>{editId ? 'Editar servicio' : 'Nuevo servicio'}</h3>
            <form onSubmit={handleSubmit}>
              {!editId && (
                <div style={fieldSt}>
                  <label style={labelSt}>ID (opcional — se genera automáticamente si lo dejas vacío)</label>
                  <input style={inputSt} value={form.id} onChange={e => setForm(f => ({ ...f, id: e.target.value }))} placeholder="ej. corte-cabello" />
                </div>
              )}
              <div style={fieldSt}>
                <label style={labelSt}>Nombre *</label>
                <input style={inputSt} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="ej. Corte de cabello" />
              </div>
              <div style={fieldSt}>
                <label style={labelSt}>Descripción</label>
                <textarea style={{ ...inputSt, resize: 'vertical', minHeight: '70px' }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Descripción del servicio" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.8rem', marginBottom: '0.8rem' }}>
                <div>
                  <label style={labelSt}>Precio</label>
                  <input style={inputSt} type="text" inputMode="numeric" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value.replace(/\D/g, '') }))} placeholder="ej: 50000" />
                </div>
                <div>
                  <label style={labelSt}>Duración * (minutos)</label>
                  <input style={inputSt} type="number" min="1" step="1" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} onWheel={e => e.currentTarget.blur()} placeholder="ej. 30" />
                </div>
              </div>
              <div style={fieldSt}>
                <label style={labelSt}>Imagen principal (se redimensiona a 500×500 automáticamente)</label>
                <input type="file" accept="image/*" onChange={e => setPrimaryImage(e.target.files[0])} style={{ color: theme.colors.text, fontSize: '0.85rem' }} />
                {primaryImage && <p style={{ color: theme.colors.success, fontSize: '0.78rem', marginTop: '3px' }}>✓ {primaryImage.name}</p>}
                {editId && !primaryImage && <p style={{ color: theme.colors.textMuted, fontSize: '0.76rem', marginTop: '3px' }}>Deja vacío para conservar la imagen actual.</p>}
              </div>
              <div style={fieldSt}>
                <label style={labelSt}>Imágenes o videos adicionales (hasta 9)</label>
                <input type="file" accept="image/*,video/*" multiple onChange={e => setAdditionalImages(Array.from(e.target.files))} style={{ color: theme.colors.text, fontSize: '0.85rem' }} />
                {additionalImages.length > 0 && <p style={{ color: theme.colors.success, fontSize: '0.78rem', marginTop: '3px' }}>✓ {additionalImages.length} archivo(s) seleccionado(s)</p>}
                <p style={{ color: theme.colors.textMuted, fontSize: '0.76rem', marginTop: '3px' }}>Puedes subir imágenes y videos; los videos se reproducen al verlos.{editId ? ' Las nuevas se agregan a las existentes.' : ''}</p>
              </div>
              {formError && <p style={{ color: '#e74c3c', fontSize: '0.85rem', margin: '0 0 0.8rem' }}>{formError}</p>}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button type="button" onClick={() => setShowForm(false)} style={{ background: 'none', border: `1px solid ${theme.colors.border}`, color: theme.colors.text, padding: '8px 18px', borderRadius: '7px', cursor: 'pointer' }}>Cancelar</button>
                <button type="submit" disabled={submitting} style={{ backgroundColor: theme.colors.primary, color: '#000', border: 'none', padding: '8px 20px', borderRadius: '7px', cursor: 'pointer', fontWeight: 600 }}>
                  {submitting ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Services;
